/**
 * uni API 兼容层
 * 将 uni-app 专有 API 映射到浏览器标准 API
 * 在 main.js 中通过 window.uni = uni 全局挂载，
 * 并在 router 创建后调用 setRouter(router) 注入路由实例。
 */

let _router = null

export function setRouter(router) {
  _router = router
}

// ─── Storage ─────────────────────────────────────────────────────────────────
function getStorageSync(key) {
  const raw = localStorage.getItem(key)
  if (raw === null || raw === undefined) return ''
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

function setStorageSync(key, value) {
  if (value === null || value === undefined || value === '') {
    localStorage.setItem(key, '')
  } else if (typeof value === 'object') {
    localStorage.setItem(key, JSON.stringify(value))
  } else {
    localStorage.setItem(key, value)
  }
}

function removeStorageSync(key) {
  localStorage.removeItem(key)
}

function clearStorageSync() {
  localStorage.clear()
}

// ─── Navigation ──────────────────────────────────────────────────────────────
function navigateTo({ url } = {}) {
  if (_router) _router.push(url).catch(() => {})
  else console.warn('[uni] router not set, cannot navigateTo', url)
}

function redirectTo({ url } = {}) {
  if (_router) _router.replace(url).catch(() => {})
  else console.warn('[uni] router not set, cannot redirectTo', url)
}

function navigateBack({ delta = 1 } = {}) {
  if (_router) _router.go(-delta)
  else history.go(-delta)
}

function reLaunch({ url } = {}) {
  if (_router) _router.replace(url).catch(() => {})
}

function switchTab({ url } = {}) {
  if (_router) _router.replace(url).catch(() => {})
}

// ─── Preload (no-op in web, Vue Router handles lazy-loading) ─────────────────
function preloadPage() {}

// ─── TabBar (no-op in web) ───────────────────────────────────────────────────
function setTabBarItem() {}
function showTabBar() {}
function hideTabBar() {}

// ─── Locale ───────────────────────────────────────────────────────────────────
function getLocale() {
  return (
    localStorage.getItem('lang') ||
    (navigator.language || 'cn').split('-')[0] ||
    'cn'
  )
}
function setLocale(locale) {
  localStorage.setItem('lang', locale)
}

// ─── WebSocket ────────────────────────────────────────────────────────────────
function connectSocket({ url, header } = {}) {
  const ws = new WebSocket(url)
  const listeners = { open: [], message: [], close: [], error: [] }

  ws.onopen = (e) => listeners.open.forEach((fn) => fn(e))
  ws.onmessage = (e) => listeners.message.forEach((fn) => fn({ data: e.data }))
  ws.onclose = (e) => listeners.close.forEach((fn) => fn(e))
  ws.onerror = (e) => listeners.error.forEach((fn) => fn(e))

  return {
    onOpen(fn) { listeners.open.push(fn) },
    onMessage(fn) { listeners.message.push(fn) },
    onClose(fn) { listeners.close.push(fn) },
    onError(fn) { listeners.error.push(fn) },
    send({ data, success, fail } = {}) {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(data)
        success && success()
      } else {
        fail && fail({ errMsg: 'WebSocket not open' })
      }
    },
    close() { ws.close() }
  }
}

function closeSocket() {
  return Promise.resolve()
}

// ─── Pages Stack (兼容 getCurrentPages) ───────────────────────────────────────
export function getCurrentPages() {
  if (!_router) return [{ route: '' }]
  const matched = _router.currentRoute.matched
  return matched.map((r) => ({ route: r.path }))
}

// ─── Misc ─────────────────────────────────────────────────────────────────────
function showToast({ title = '', duration = 1500 } = {}) {
  console.log('[uni.showToast]', title)
}
function hideToast() {}
function showLoading({ title = '' } = {}) {
  console.log('[uni.showLoading]', title)
}
function hideLoading() {}

// ─── Export ──────────────────────────────────────────────────────────────────
const uni = {
  // Storage
  getStorageSync,
  setStorageSync,
  removeStorageSync,
  clearStorageSync,
  // Navigation
  navigateTo,
  redirectTo,
  navigateBack,
  reLaunch,
  switchTab,
  // Preload (no-op)
  preloadPage,
  // TabBar
  setTabBarItem,
  showTabBar,
  hideTabBar,
  // Locale
  getLocale,
  setLocale,
  // WebSocket
  connectSocket,
  closeSocket,
  // Misc
  showToast,
  hideToast,
  showLoading,
  hideLoading
}

export default uni
